import React from "react";
import { TextField } from "@mui/material";
import {
  FormContainer,
  FormGrid,
  FormSection,
  FormWithChecklistWrapper,
  InlineHelp,
  SectionDescription,
  SectionTitle,
} from "./styles";
import { PendenciesChecklist } from "./checklist";
import { IBusinessInfo } from "../../types";

interface BusinessInfoFormProps {
  formData: IBusinessInfo;
  onChange: (
    field: string,
    value:
      | string
      | IBusinessInfo["enderecoComercial"]
      | IBusinessInfo["legalRepresentative"]
      | IBusinessInfo["accountManager"],
  ) => void;
}

export const BusinessInfoForm: React.FC<BusinessInfoFormProps> = ({
  formData,
  onChange,
}) => {
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  };

  const checklistItems = [
    {
      id: "company",
      label: "Empresa identificada",
      checked:
        formData.razaoSocial.trim() !== "" && formData.cpfCnpj.trim() !== "",
    },
    {
      id: "contact",
      label: "Contato para retorno",
      checked: formData.telefone.trim() !== "" && formData.email.trim() !== "",
    },
    {
      id: "address",
      label: "Endereço encontrado",
      checked:
        formData.enderecoComercial.rua.trim() !== "" &&
        formData.enderecoComercial.bairro.trim() !== "" &&
        formData.enderecoComercial.cidade.trim() !== "" &&
        formData.enderecoComercial.estado.trim() !== "",
    },
    {
      id: "goal",
      label: "Objetivo explicado",
      checked: (formData.extraDescription || "").trim() !== "",
    },
  ];

  return (
    <FormWithChecklistWrapper>
      <FormContainer>
        <FormSection>
          <SectionTitle>
            <span className="material-icons">business</span>
            Dados da empresa
          </SectionTitle>
          <SectionDescription>
            Use os dados do negócio que vai contratar o GeoRanking. Se for MEI
            ou autônomo, pode informar o CPF/CNPJ usado na contratação.
          </SectionDescription>
          <FormGrid>
            <TextField
              label="Nome da empresa ou razão social"
              value={formData.razaoSocial}
              onChange={(e) => onChange("razaoSocial", e.target.value)}
              fullWidth
              required
              variant="outlined"
              placeholder="Ex.: Borracharia Avenida"
            />
            <TextField
              label="CPF/CNPJ"
              value={formData.cpfCnpj}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                const formatted =
                  value.length <= 11
                    ? value.replace(
                        /(\d{3})(\d{3})(\d{3})(\d{0,2})/,
                        "$1.$2.$3-$4",
                      )
                    : value.replace(
                        /(\d{2})(\d{3})(\d{3})(\d{0,4})/,
                        "$1.$2.$3/$4-",
                      );
                onChange("cpfCnpj", formatted);
              }}
              fullWidth
              required
              variant="outlined"
              placeholder="00.000.000/0000-00"
              inputProps={{ maxLength: 18 }}
            />
            <TextField
              label="WhatsApp ou telefone"
              value={formData.telefone}
              onChange={(e) => onChange("telefone", formatPhone(e.target.value))}
              fullWidth
              required
              variant="outlined"
              placeholder="(00) 00000-0000"
              inputProps={{ maxLength: 15, inputMode: "numeric" }}
            />
            <TextField
              label="E-mail para receber a contratação"
              value={formData.email}
              onChange={(e) => onChange("email", e.target.value)}
              fullWidth
              required
              type="email"
              variant="outlined"
              placeholder="seuemail@dominio.com"
            />
          </FormGrid>
        </FormSection>

        <FormSection>
          <SectionTitle>
            <span className="material-icons">place</span>
            Endereço principal
          </SectionTitle>
          <SectionDescription>
            Informe o endereço da empresa ou o ponto de partida do atendimento.
            Isso ajuda a sugerir cidade, bairro e raio de alcance.
          </SectionDescription>
          <FormGrid>
            <TextField
              label="Rua e número"
              value={formData.enderecoComercial.rua}
              onChange={(e) =>
                onChange("enderecoComercial", {
                  ...formData.enderecoComercial,
                  rua: e.target.value,
                })
              }
              fullWidth
              required
              variant="outlined"
              placeholder="Ex.: Av. Brasil, 120"
            />
            <TextField
              label="Bairro"
              value={formData.enderecoComercial.bairro}
              onChange={(e) =>
                onChange("enderecoComercial", {
                  ...formData.enderecoComercial,
                  bairro: e.target.value,
                })
              }
              fullWidth
              required
              variant="outlined"
              placeholder="Ex.: Centro"
            />
            <TextField
              label="Cidade"
              value={formData.enderecoComercial.cidade}
              onChange={(e) =>
                onChange("enderecoComercial", {
                  ...formData.enderecoComercial,
                  cidade: e.target.value,
                })
              }
              fullWidth
              required
              variant="outlined"
              placeholder="Ex.: Goiânia"
            />
            <TextField
              label="Estado"
              value={formData.enderecoComercial.estado}
              onChange={(e) =>
                onChange("enderecoComercial", {
                  ...formData.enderecoComercial,
                  estado: e.target.value.toUpperCase(),
                })
              }
              fullWidth
              required
              variant="outlined"
              placeholder="GO"
              inputProps={{ maxLength: 2 }}
            />
            <TextField
              label="Complemento ou referência"
              value={formData.enderecoComercial.complemento}
              onChange={(e) =>
                onChange("enderecoComercial", {
                  ...formData.enderecoComercial,
                  complemento: e.target.value,
                })
              }
              fullWidth
              variant="outlined"
              placeholder="Ao lado do posto, sala 2, loja 3..."
              sx={{ gridColumn: "1 / -1" }}
            />
          </FormGrid>
        </FormSection>

        <FormSection>
          <SectionTitle>
            <span className="material-icons">record_voice_over</span>
            Objetivo da contratação
          </SectionTitle>
          <SectionDescription>
            Escreva em linguagem simples quais serviços quer vender mais. A IA
            usa esse contexto para sugerir categorias, palavras-chave e
            descrição.
          </SectionDescription>
          <InlineHelp>
            Exemplo: Quero aparecer para quem procura borracharia 24 horas,
            troca de pneu e conserto de pneu no Centro e bairros próximos.
          </InlineHelp>
          <FormGrid>
            <TextField
              value={formData.extraDescription ?? ""}
              onChange={(e) => onChange("extraDescription", e.target.value)}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Conte em poucas palavras o que sua empresa faz e o que quer vender mais."
              sx={{ gridColumn: "1 / -1" }}
            />
          </FormGrid>
        </FormSection>
      </FormContainer>

      <PendenciesChecklist items={checklistItems} />
    </FormWithChecklistWrapper>
  );
};
